import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { fetchClientProjects } from '@/features/clients/clientsSlice'
import { selectClientProjects } from '@/features/clients/clientsSelectors'
import type { ApiClientRow } from '@/features/clients/types'
import { useAppDispatch, useAppSelector } from '@/store'

/**
 * The expanded client detail (PX-W6.2): profile fields, the contacts table,
 * commercial terms and the linked projects (fetched once per client through
 * the slice's cache).
 */
export function ClientDetailPanel({ row }: { row: ApiClientRow }) {
  const { t } = useTranslation('clients')
  const dispatch = useAppDispatch()
  const projects = useAppSelector((s) => selectClientProjects(s, row.id))

  useEffect(() => {
    void dispatch(fetchClientProjects(row.id))
  }, [row.id, dispatch])

  const { profile, commercialTerms, contacts = [] } = row

  return (
    <div className="grid gap-4 border-t border-border px-4 py-4 lg:grid-cols-3" data-testid="client-detail">
      <section aria-labelledby={`client-profile-${row.id}`}>
        <div id={`client-profile-${row.id}`}>
          <SectionLabel>{t('detail.profile')}</SectionLabel>
        </div>
        <dl className="mt-2 space-y-1.5 text-sm">
          <div className="flex gap-2">
            <dt className="text-muted-foreground">{t('detail.industry')}</dt>
            <dd className="text-foreground">{profile?.industry ?? '—'}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-muted-foreground">{t('detail.website')}</dt>
            <dd className="min-w-0 truncate text-foreground">{profile?.website ?? '—'}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-muted-foreground">{t('detail.notes')}</dt>
            <dd className="text-foreground">{profile?.notes ?? '—'}</dd>
          </div>
        </dl>
      </section>

      <section aria-labelledby={`client-contacts-${row.id}`}>
        <div id={`client-contacts-${row.id}`}>
          <SectionLabel>{t('detail.contacts')}</SectionLabel>
        </div>
        {contacts.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">{t('detail.noContacts')}</p>
        ) : (
          <ul className="mt-2 space-y-1.5 text-sm">
            {contacts.map((contact, index) => (
              <li key={index} className="leading-tight">
                <span className="font-medium text-foreground">{contact.name ?? contact.email ?? '—'}</span>
                {contact.role !== undefined && contact.role !== '' && (
                  <span className="block text-xs text-muted-foreground">{contact.role}</span>
                )}
                {contact.email !== undefined && contact.email !== '' && (
                  <span className="block text-xs text-ink-soft">{contact.email}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby={`client-terms-${row.id}`}>
        <div id={`client-terms-${row.id}`}>
          <SectionLabel>{t('detail.terms')}</SectionLabel>
        </div>
        <dl className="mt-2 space-y-1.5 text-sm">
          <div className="flex gap-2">
            <dt className="text-muted-foreground">{t('detail.currency')}</dt>
            <dd className="text-foreground">{commercialTerms?.currency ?? '—'}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-muted-foreground">{t('detail.paymentTerms')}</dt>
            <dd className="text-foreground">
              {commercialTerms?.paymentTermsDays !== undefined
                ? t('detail.paymentTermsDays', { days: commercialTerms.paymentTermsDays })
                : '—'}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-muted-foreground">{t('detail.defaultRate')}</dt>
            <dd className="text-foreground">{commercialTerms?.defaultRate ?? '—'}</dd>
          </div>
        </dl>

        <div className="mt-4">
          <SectionLabel>{t('detail.projects')}</SectionLabel>
          {projects.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">{t('detail.noProjects')}</p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {projects.map((project) => (
                <StatusPill key={project.id} tone="proposed">
                  {project.name ?? project.id}
                </StatusPill>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
